import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Typography, Divider, Grid, Backdrop } from '@material-ui/core';
import { CircularProgress } from '@mui/material';
import { Search as SearchIcon, BackspaceOutlined as BackspaceOutlinedIcon } from '@material-ui/icons';
import { subDays, startOfDay, endOfDay, isAfter, isBefore } from 'date-fns';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';

import DateField from '../../components/Form/Date';
import TextField from '../../components/Form/TextField';
import PageTitle from '../../components/ui/PageTitleUI';
import ButtonUI from '../../components/ui/ButtonUI';
import { useAuth } from '../../hooks/auth';
import MvmApi from '../../services/MvmApi';
import permissions from '../../utils/Permissions';
import { useStyles } from '../../utils/DefaultStyles';
import exportExcelFileFrom from '../../utils/xlsxExport';
import utilsDate from '../../utils/Date';

interface IFormPanel {
    startDate: Date | null;
    endDate: Date | null;
    mvmNumber: string;
}

const PendingAccountingReport: React.FC = () => {
    const classes = useStyles();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const { user, hasPermission } = useAuth();
    const { t } = useTranslation('Reports');
    const { t: _t } = useTranslation('General');
    const { t: tVal } = useTranslation('Validation');
    const [loading, setLoading] = useState(false);

    const minAllowedDate = useMemo(() => startOfDay(subDays(new Date(), 30)), []);

    const { handleSubmit, reset, control, clearErrors, setError } = useForm<IFormPanel>({
        defaultValues: {
            startDate: subDays(new Date(), 7),
            endDate: new Date(),
            mvmNumber: ''
        }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const hasReportPermission = hasPermission(permissions.REPORTS.PendingAccounting);
        const hasMonitorPermission = hasPermission(permissions.MVM.PendingAccount);

        if (!hasReportPermission && !hasMonitorPermission)
            history.replace('/NoPermission');
    }, [history, user]);

    const formValidation = async (data: IFormPanel) => {
        clearErrors();

        try {
            const schema = Yup.object().shape({
                startDate: Yup.date().required(_t('requiredFieldValidation')).typeError(tVal('formatDate')),
                endDate: Yup.date().required(_t('requiredFieldValidation')).typeError(tVal('formatDate')),
                mvmNumber: Yup.string().nullable()
            });

            await schema.validate(data, {
                abortEarly: false,
            });

            const parsedStartDate = utilsDate.ParseInputDate(data.startDate as Date | string | null);
            const parsedEndDate = utilsDate.ParseInputDate(data.endDate as Date | string | null);

            const startDate = parsedStartDate ? startOfDay(parsedStartDate) : null;
            const endDate = parsedEndDate ? endOfDay(parsedEndDate) : null;

            if (!startDate || !endDate)
                return false;

            if (isBefore(startDate, minAllowedDate)) {
                setError('startDate', { message: t('pendingAccounting.max90DaysValidation') });
                return false;
            }

            if (isAfter(startDate, endDate)) {
                setError('endDate', { message: t('pendingAccounting.invalidDateRangeValidation') });
                return false;
            }

            return true;
        }
        catch (err) {
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {
                    setError(error.path?.split('.')[0] as 'startDate' | 'endDate' | 'mvmNumber' , {
                        message: error.message
                    });
                });
            }

            return false;
        }
    };

    const onSubmit = async (data: IFormPanel) => {
        const validForm = await formValidation(data);
        if (!validForm)
            return;

        try {
            setLoading(true);
            const parsedStartDate = utilsDate.ParseInputDate(data.startDate as Date | string | null);
            const parsedEndDate = utilsDate.ParseInputDate(data.endDate as Date | string | null);

            const startDate = startOfDay(parsedStartDate as Date);
            const endDate = endOfDay(parsedEndDate as Date);
            const mvmFilter = (data.mvmNumber || '').trim().toLowerCase();

            const result = await MvmApi.PendingAccountingResolutionByDateMonitor(
                user.company.plantID,
                startDate,
                endDate,
                mvmFilter
            );

         
            const exportData = result.map((item: any) => {
                const blockUser = item?.accountingPendingResolutionUser
                    ? `${item.accountingPendingResolutionUser}(${item.accountingPendingResolutionUserName || ''})`
                    : '';

                const baseData: Record<string, string | number> = {
                    [t('pendingAccounting.columns.mvmNumber')]: item?.mvmNumber || item?.mvcNumber || '',
                    [t('pendingAccounting.columns.invoiceKey')]: item?.invoiceKey || '',
                    [t('pendingAccounting.columns.invoiceNumber')]: item?.invoiceNumber || '',
                    [t('pendingAccounting.columns.supplier')]: item?.supplier || '',
                    [t('pendingAccounting.columns.invoiceDate')]: utilsDate.DateTimeShort(item?.invoiceDate),
                    [t('pendingAccounting.columns.category')]: item?.typeMvm === 'S' ? t('pendingAccounting.jit') : t('pendingAccounting.kanban'),
                    [t('pendingAccounting.columns.companyCodeLegacy')]: item?.companyCodeLegacy,
                    [t('pendingAccounting.columns.status')]: item?.statusDescription || '',
                    [t('pendingAccounting.columns.blockUser')]: blockUser,
                    [t('pendingAccounting.columns.blockDateTime')]: utilsDate.DateTimeShort(item?.accountingPendingResolutionDate)
                };
                return baseData;
            });

            if (exportData.length === 0) {
                enqueueSnackbar(t('pendingAccounting.noDataForFilter'), { variant: 'warning' });
                return;
            }

            exportExcelFileFrom(exportData, null, 'pending-accounting-report');

            enqueueSnackbar(t('pendingAccounting.exportSuccess', { count: exportData.length }), {
                variant: 'success'
            });
        }
        catch (err: any) {
            enqueueSnackbar(err?.message || t('pendingAccounting.exportError'), { variant: 'error' });
        }
        finally {
            setLoading(false);
        }
    };

    const cleanFilters = () => {
        reset({
            startDate: subDays(new Date(), 7),
            endDate: new Date(),
            mvmNumber: ''
        });
    };

    return (
        <Container maxWidth={false} component="main">
            <Backdrop style={{ zIndex: 9999 }} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <PageTitle title={t('pendingAccounting.pageTitle')} />

            <div className={classes.rootContent}>
                <div className={classes.paper}>
                    <form translate={undefined} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={1}>
                            <Grid item xs={6} style={{ padding: '0px', display: 'flex', alignItems: 'center' }}>
                                <Typography className={classes.root}>
                                    {t('titleParameters')}
                                </Typography>
                            </Grid>

                            <Grid item xs={6} style={{ padding: '0px', textAlign: 'right' }}>
                                <ButtonUI onClick={() => cleanFilters()} startIcon={<BackspaceOutlinedIcon />}>
                                    {_t('clean')}
                                </ButtonUI>
                                <ButtonUI type="submit" color="primary" startIcon={<SearchIcon />}>
                                    {_t('export')}
                                </ButtonUI>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '10px' }} />

                        <Grid container spacing={1}>
                            <Grid item xs={2}>
                                <DateField
                                    label={t('pendingAccounting.startDate')}
                                    name="startDate"
                                    control={control}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={2}>
                                <DateField
                                    label={t('pendingAccounting.endDate')}
                                    name="endDate"
                                    control={control}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={2}>
                                <TextField
                                    label={t('pendingAccounting.mvmNumber')}
                                    name="mvmNumber"
                                    control={control}
                                    type="text"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </form>
                </div>
            </div>
        </Container>
    );
};

export default PendingAccountingReport;
